import React from 'react';
import {View, Text, TouchableOpacity, StyleSheet} from 'react-native';
import {Colors} from '../../../constants/Colors';

interface IPendingPayment {
  id: string;
  client_code: string;
  reference_type: string;
  reference_id: string;
  transaction_id: string;
  operation_id: string;
  status: string;
  pix_copy_paste: string;
  confirmed: boolean;
  amount: number;
  pix_key: string;
  pix_qrcode_url: string;
  statement_descriptor: string;
  expiration_date: string;
  expected_on: string;
  created_at: string;
  updated_at: string;
}

interface IPendingPaymentCardProps {
  item: IPendingPayment;
  onPress: () => void;
}

const getStatusInfo = (status: string) => {
  switch (status) {
    case 'succeeded':
      return {label: 'Pago', color: 'green'};
    case 'charged_back':
    case 'refunded':
      return {label: 'Ressarcido', color: 'green'};
    case 'failed':
      return {label: 'Falhou', color: 'red'};
    case 'canceled':
      return {label: 'Pagamento cancelado', color: 'red'};
    case 'pre_authorized':
      return {label: 'Pré-autorizado', color: 'orange'};
    case 'reversed':
      return {label: 'Estornado', color: 'orange'};
    case 'pending':
      return {label: 'Pagamento pendente', color: 'orange'};
    case 'new':
      return {label: 'Novo', color: 'orange'};
    case 'partial_refunded':
      return {label: 'Reembolso parcial', color: 'orange'};
    case 'dispute':
      return {label: 'Em disputa', color: 'orange'};
    case 'unpaid':
      return {label: 'Não pago', color: 'orange'};
    default:
      return {label: 'Desconhecido', color: 'gray'};
  }
};

const PendingPaymentCard = ({item, onPress}: IPendingPaymentCardProps) => {
  const now = new Date();
  const expirationDate = new Date(item.expiration_date);
  const isExpired = expirationDate < now;

  const statusInfo = isExpired
    ? {label: 'Pagamento expirado', color: 'gray'}
    : getStatusInfo(item.status);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Sao_Paulo',
    });
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Text style={styles.transactionId}>
            {isExpired ? 'Pagamento pix expirado' : 'Aguardando pagamento pix'}
          </Text>
          <Text style={styles.date}>
            Criado em: {formatDateTime(item.created_at)}
          </Text>
        </View>
        <View style={styles.statusBadge}>
          <Text style={[styles.statusText, {color: statusInfo.color}]}>
            {statusInfo.label}
          </Text>
        </View>
      </View>

      <View style={styles.divider} />

      <View style={styles.content}>
        <View style={styles.infoRow}>
          <Text style={styles.label}>Valor:</Text>
          <Text style={styles.value}>{formatCurrency(item.amount)}</Text>
        </View>

        {item.expiration_date && (
          <View style={styles.infoRow}>
            <Text style={styles.label}>Vencimento:</Text>
            <Text
              style={[
                styles.valueExpiration,
                isExpired && {color: Colors.GrayPallete.lightgrayThree},
              ]}>
              {formatDateTime(item.expiration_date)}
            </Text>
          </View>
        )}
      </View>

      {!isExpired && (
        <TouchableOpacity style={styles.button} onPress={onPress}>
          <Text style={styles.buttonText}>Ir para pagamento</Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 3,
    borderWidth: 1,
    borderColor: Colors.RedPallete.red + '20',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  headerLeft: {
    flex: 1,
  },
  transactionId: {
    fontSize: 14,
    fontFamily: 'TCCC-UnityText Bold',
    color: 'black',
    marginBottom: 4,
  },
  date: {
    fontSize: 12,
    fontFamily: 'TCCC-UnityHeadline Regular',
    color: Colors.GrayPallete.lightgrayThree,
  },
  statusBadge: {
    backgroundColor: Colors.RedPallete.red + '15',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 11,
    fontFamily: 'TCCC-UnityText Bold',
    textTransform: 'uppercase',
  },
  divider: {
    height: 1,
    backgroundColor: Colors.GrayPallete.lightgrayThree + '30',
    marginVertical: 12,
  },
  content: {
    marginBottom: 16,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  label: {
    fontSize: 13,
    fontFamily: 'TCCC-UnityHeadline Regular',
    color: Colors.GrayPallete.lightgrayThree,
  },
  value: {
    fontSize: 14,
    fontFamily: 'TCCC-UnityText Bold',
    color: 'black',
  },
  valueExpiration: {
    fontSize: 14,
    fontFamily: 'TCCC-UnityText Bold',
    color: Colors.RedPallete.red,
  },
  button: {
    backgroundColor: Colors.RedPallete.red,
    borderRadius: 8,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    color: 'white',
    fontSize: 14,
    fontFamily: 'TCCC-UnityText Bold',
    lineHeight: 16,
  },
});

export default PendingPaymentCard;
